import React, { useState, useCallback, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * BlogEditor Component
 * 
 * A rich text editor for blog posts with:
 * - Full HTML formatting support
 * - Image upload to server
 * - Auto-save functionality
 * - Preview mode
 */

// Quill toolbar configuration
const TOOLBAR_OPTIONS = [
  [{ 'header': [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  [{ 'color': [] }, { 'background': [] }],
  ['clean']
];

// Quill formats
const FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'blockquote', 'code-block',
  'link', 'image',
  'color', 'background'
];

const BlogEditor = ({
  value = '',
  onChange,
  placeholder = 'Start writing your blog post...',
  onAutoSave,
  autoSaveInterval = 30000,
  className = '',
  readOnly = false
}) => {
  const [lastSaved, setLastSaved] = useState(null);
  const quillRef = useRef(null);
  const autoSaveTimerRef = useRef(null);

  // Handle content change
  const handleChange = useCallback((content, delta, source, editor) => {
    if (onChange) {
      onChange(content);
    }

    // Set up auto-save
    if (onAutoSave && autoSaveInterval > 0) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      autoSaveTimerRef.current = setTimeout(() => {
        onAutoSave(content);
        setLastSaved(new Date());
      }, autoSaveInterval);
    }
  }, [onChange, onAutoSave, autoSaveInterval]);

  // Get word count
  const wordCount = useMemo(() => {
    if (!value) return 0;
    // Strip HTML and count words
    const text = value.replace(/<[^>]*>/g, '').trim();
    return text ? text.split(/\s+/).length : 0;
  }, [value]);

  // Estimated read time
  const readTime = useMemo(() => {
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  }, [wordCount]);

  // Custom image handler - uploads to server
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error('Image too large. Maximum size is 10MB.');
          return;
        }

        // Upload to server
        const formData = new FormData();
        formData.append('file', file);
        
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${API_URL}/admin/blog/upload-image`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.data.success && response.data.url) {
            const quill = quillRef.current?.getEditor();
            if (quill) {
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', response.data.url);
              // Move cursor after the image
              quill.setSelection(range.index + 1);
            }
            toast.success('Image uploaded successfully!');
          } else {
            throw new Error('Failed to get image URL');
          }
        } catch (error) {
          console.error('Image upload failed:', error);
          toast.error(error.response?.data?.detail || 'Failed to upload image');
          
          // Fallback to base64 if upload fails
          const reader = new FileReader();
          reader.onload = (e) => {
            const quill = quillRef.current?.getEditor();
            if (quill) {
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', e.target.result);
            }
            toast.success('Image added (stored locally)');
          };
          reader.readAsDataURL(file);
        }
      }
    };
  }, []);

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR_OPTIONS,
      handlers: {
        image: imageHandler
      }
    }
  }), [imageHandler]);

  return (
    <div className={`blog-editor ${className}`}>
      {/* Editor */}
      <div className="editor-container bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={FORMATS}
          placeholder={placeholder}
          readOnly={readOnly}
          className="blog-quill-editor"
        />
      </div>

      {/* Footer with stats */}
      <div className="editor-footer mt-2 flex justify-between items-center text-sm text-gray-400">
        <div className="stats flex gap-4">
          <span>{wordCount} words</span>
          <span>{readTime}</span>
        </div>
        {lastSaved && (
          <div className="autosave-indicator">
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Custom styles for dark theme */}
      <style>{`
        .blog-quill-editor .ql-toolbar {
          background: #1f2937;
          border-color: #374151;
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
        }
        
        .blog-quill-editor .ql-toolbar .ql-stroke {
          stroke: #9ca3af;
        }
        
        .blog-quill-editor .ql-toolbar .ql-fill {
          fill: #9ca3af;
        }
        
        .blog-quill-editor .ql-toolbar .ql-picker {
          color: #9ca3af;
        }
        
        .blog-quill-editor .ql-toolbar button:hover .ql-stroke,
        .blog-quill-editor .ql-toolbar button.ql-active .ql-stroke {
          stroke: #ec4899;
        }
        
        .blog-quill-editor .ql-toolbar button:hover .ql-fill,
        .blog-quill-editor .ql-toolbar button.ql-active .ql-fill {
          fill: #ec4899;
        }
        
        .blog-quill-editor .ql-container {
          background: #1f2937;
          border-color: #374151;
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
          min-height: 400px;
          font-size: 1rem;
        }
        
        .blog-quill-editor .ql-editor {
          color: #e5e7eb;
          min-height: 400px;
        }
        
        .blog-quill-editor .ql-editor.ql-blank::before {
          color: #6b7280;
          font-style: italic;
        }
        
        .blog-quill-editor .ql-editor h1 {
          color: white;
          font-size: 2rem;
          font-weight: 700;
        }
        
        .blog-quill-editor .ql-editor h2 {
          color: white;
          font-size: 1.5rem;
          font-weight: 600;
          border-left: 4px solid #ec4899;
          padding-left: 1rem;
          margin-top: 2rem;
        }
        
        .blog-quill-editor .ql-editor h3 {
          color: #f472b6;
          font-size: 1.25rem;
          font-weight: 600;
        }
        
        .blog-quill-editor .ql-editor a {
          color: #f472b6;
        }
        
        .blog-quill-editor .ql-editor blockquote {
          border-left: 4px solid #8b5cf6;
          background: rgba(139, 92, 246, 0.1);
          padding: 1rem;
          margin: 1rem 0;
          color: #d1d5db;
        }
        
        .blog-quill-editor .ql-editor pre.ql-syntax {
          background: #111827;
          border-radius: 0.5rem;
          color: #e5e7eb;
          padding: 1rem;
          overflow-x: auto;
        }
        
        .blog-quill-editor .ql-editor img {
          max-width: 100%;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        
        .blog-quill-editor .ql-editor ul,
        .blog-quill-editor .ql-editor ol {
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        
        .blog-quill-editor .ql-editor li {
          color: #d1d5db;
          margin: 0.5rem 0;
        }
        
        .blog-quill-editor .ql-picker-options {
          background: #1f2937;
          border-color: #374151;
        }
        
        .blog-quill-editor .ql-picker-item {
          color: #9ca3af;
        }
        
        .blog-quill-editor .ql-picker-item:hover {
          color: #ec4899;
        }
      `}</style>
    </div>
  );
};

export default BlogEditor;

